import { useState, useCallback } from 'react';

/**
 * Хук для работы с камерой устройства
 * Обрабатывает запрос разрешений, захват фото и конвертацию в File
 */
export const useCameraCapture = () => {
  const [isCapturing, setIsCapturing] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Захватывает фото с камеры устройства
   * @returns {Promise<File | null>} - файл изображения или null при ошибке
   */
  const capturePhoto = useCallback(async () => {
    setIsCapturing(true);
    setError(null);

    try {
      // Проверка поддержки MediaDevices API
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Камера не поддерживается в этом браузере');
      }

      // Запрос доступа к камере
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user', // фронтальная камера
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });

      // Создание video элемента для захвата кадра
      const video = document.createElement('video');
      video.srcObject = stream;
      video.autoplay = true;
      video.playsInline = true;

      // Ожидание готовности видео
      await new Promise((resolve, reject) => {
        video.onloadedmetadata = () => {
          video.play()
            .then(() => resolve())
            .catch(reject);
        };
        video.onerror = reject;
      });

      // Создание canvas для захвата кадра
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Остановка потока
      stream.getTracks().forEach(track => track.stop());
      video.srcObject = null;

      // Конвертация canvas в Blob, затем в File
      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Ошибка при создании изображения'));
              return;
            }

            const file = new File(
              [blob],
              `camera-photo-${Date.now()}.jpg`,
              { type: 'image/jpeg' }
            );

            setIsCapturing(false);
            resolve(file);
          },
          'image/jpeg',
          0.95 // качество JPEG
        );
      });
    } catch (err) {
      setIsCapturing(false);

      // Обработка различных типов ошибок
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Доступ к камере запрещен. Разрешите доступ в настройках браузера');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setError('Камера не найдена');
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setError('Камера уже используется другим приложением');
      } else if (err.name === 'OverconstrainedError' || err.name === 'ConstraintNotSatisfiedError') {
        setError('Камера не поддерживает требуемые параметры');
      } else {
        setError(err.message || 'Ошибка при использовании камеры');
      }

      return null;
    }
  }, []);

  return {
    capturePhoto,
    isCapturing,
    error,
    setError
  };
};

export default useCameraCapture;

